import { createTheme } from '@mui/material/styles';

import chevron from 'images/icons/chevron.svg';
import breakpoints from 'utils/breakpoints';
import { getColor } from 'utils/colors';
import mixins from 'utils/mixins';
import { remCalc } from 'utils/remCalc';
import { typography as type } from 'utils/typography';

const theme = createTheme({
	breakpoints: breakpoints,
	mixins: mixins,
});

const typography = type.typography;

export const overrides = {
	'.mainNav': {
		position: 'fixed',
		width: '100vw',
		display: 'flex',
		flexDirection: 'column',
		...theme.mixins.glass,
		zIndex: 'var(--mui-zIndex-navBar)',
		transitionTimingFunction: 'var(--transition-timing)',
		transitionProperty: 'background, color',
		transitionDuration: 'var(--transition-duration)',
		'*': {
			transitionTimingFunction: 'var(--transition-timing)',
			transitionProperty: 'background, color, padding',
			transitionDuration: 'var(--transition-duration)',
		},

		'.mainNav__mainContainer': {
			padding: '16px',
			[theme.breakpoints.up('sm')]: {
				padding: '20px 73px',
			},
			marginLeft: 'calc(var(--space-gap) * -1)',
			'.mainNav__menuButton': {
				marginLeft: 0,
				padding: 0,
				paddingRight: '50px',
				display: 'flex',
				alignItems: 'center',
				'.mainNav__menuButton__iconContainer': {
					padding: '8.5px 11.5px',
					marginRight: '16px',
					borderRadius: '6px',
					display: 'flex',
					alignItems: 'center',
					minHeight: '42px',
				},
				fontSize: remCalc(14),
				svg: {
					width: remCalc(20),
				},
			},
			'.mainNav__chatButton': {
				padding: '10px 15px',
				fontSize: remCalc(14),
				svg: {
					width: remCalc(32),
					[theme.breakpoints.up('sm')]: {
						width: remCalc(28),
						marginLeft: '10px',
					},
					
				},
			},

			'>div': {
				alignItems: 'center',
			},
			'.mainNav__button__label': {
				display: 'none',
				[theme.breakpoints.up('sm')]: {
					display: 'inline',
				},
			},
			'.mainNav__logoContainer__logo-small': {
				transitionProperty: 'all',
				width: remCalc(130),
				color: 'white',
				[theme.breakpoints.up('ms')]: {
					display: 'none',
				},
			},
			'.mainNav__logoContainer__logo-large': {
				transitionProperty: 'all',
				width: remCalc(238),
				color: 'white',
				display: 'none',
				[theme.breakpoints.up('ms')]: {
					display: 'block',
				},
			},
		},

		// BEGIN FLUID GRID SUPPLEMENT
		'.MuiBox-root > div:nth-of-type(1)': {
			justifySelf: 'left',
		},
		'.MuiBox-root > div:nth-of-type(2)': {
			justifySelf: 'center',
		},
		'.MuiBox-root > div:nth-of-type(3)': {
			justifySelf: 'right',
		},
		// END FLUID GRID SUPPLEMENT

		'&.mainNav--transparent, &.mainNav--smoke, &.mainNav--initial--transparent, &.mainNav--initial--smoke': {
			color: 'white',
			'.mainNav__menuButton': {
				color: 'white',
				'.mainNav__menuButton__iconContainer': {
					...theme.mixins.glass,
				},
				'&:hover': {
					'.mainNav__menuButton__iconContainer': {
						color: getColor('ivory', 800),
						background: 'white',
					},
				}
			},
			'.mainNav__chatButton': {
				color: 'white',
				[theme.breakpoints.up('sm')]: {
					...theme.mixins.glass,
				},
				'&:hover': {
					color: getColor('ivory', 800),
					background: 'white',
				},
			},
		},

		'&.mainNav--smoke, &.mainNav--initial--smoke': {
			background: 'rgba(0, 0, 0, .35)',
		},

		'&.mainNav--opaque, &.mainNav--initial--opaque': {
			background: getColor('ivory', 400),
			color: getColor('ivory', 800),
			'.mainNav__menuButton': {
				color: getColor('ivory', 800),
				'.mainNav__menuButton__iconContainer': {
					background: 'white',
				},
				'&:hover': {
					'.mainNav__menuButton__iconContainer': {
						background: 'rgba(0, 0, 0, .1)',
						color: 'white',
					},
				},
			},
			'.mainNav__chatButton': {
				[theme.breakpoints.up('sm')]: {
					background: 'white',
				},
				color: getColor('ivory', 800),
				'&:hover': {
					background: 'rgba(0, 0, 0, .1)',
					color: 'white',
				},
			},
			'.mainNav__logoContainer__logo-small, .mainNav__logoContainer__logo-large': {
				color: getColor('ivory', 800),
			},
		},

		'&.mainNav--isOpen': {
			height: '100vh',
		},

		'&.mainNav--small': {
			'.mainNav__mainContainer': {
				padding: '4px 8px',
				[theme.breakpoints.up('sm')]: {
					padding: '10px 73px',
				},
			},
			'.mainNav__logoContainer__logo-small': {
				width: remCalc(100),
			},
			'.mainNav__logoContainer__logo-large': {
				width: remCalc(171),
			},
		},
	},

	'.mainNav__dropdown': {
		display: 'none',
		color: getColor('ivory', 600),
		flexGrow: 1,
		[theme.breakpoints.up('sm')]: {
			overflowY: 'auto',
		},
		'&.mainNav__dropdown--isOpen': {
			display: 'grid',
			[theme.breakpoints.down('sm')]: {
				...theme.mixins.glass,
			},
			[theme.breakpoints.up('sm')]: {
				borderTop: `1px solid ${getColor('ivory', 800)}`,
			}
		},
		'.mainNav__gridContainer': {
			gap: '0px',
			'.mainNav__dropdown__discover, .mainNav__dropdown__experiences, .mainNav__dropdown__planYourTrip': {
				[theme.breakpoints.up('sm')]: {
					padding: `clamp(20px, calc(100vh * 0.05), 76px) ${remCalc(73)}`,
				},
			},
			[theme.breakpoints.up('sm')]: {
				display: 'grid',
				gridTemplateColumns: '1fr 1fr',
				'>div:nth-of-type(odd)': {
					borderRight: `1px solid ${getColor('ivory', 800)}`,
					borderBottom: `1px solid ${getColor('ivory', 800)}`,
				},
				'>div:nth-of-type(even)': {
					borderBottom: `1px solid ${getColor('ivory', 800)}`,
				},
				'.mainNav__gridContainer2': {
					gap: '0px',
					display: 'grid',
					gridTemplateRows: '1fr 1fr',
					'>div': {
						display: 'flex',
						padding: `clamp(20px, 30px, 30px) ${remCalc(30)}`,
						justifyContent: 'center',
						alignItems: 'center',
					},
					'>div:nth-of-type(odd)': {
						borderBottom: `1px solid ${getColor('ivory', 800)}`,
					},
				},
			},
		},
		'.mainNav__dropdown__sectionHeader__text': {
			fontWeight: 400,
			textTransform: 'uppercase',
			letterSpacing: '-0.32px',
			leadingTrim: 'both',
			textEdge: 'cap',
			position: 'relative',
			'.mainNav__dropdown__sectionHeader__text__longArrow': {
				display: 'none',
				[theme.breakpoints.up('sm')]: {
					display: 'inline-block',
					width: remCalc(55),
					position: 'absolute',
					marginLeft: remCalc(16),
					top: '50%',
					transform: 'translateY(-50%)',
					opacity: 0,
					clipPath: 'inset(0 100% 0 0)',
					transitionTimingFunction: 'var(--transition-timing)',
					transitionProperty: 'opacity, clip-path',
					transitionDuration: 'var(--transition-duration-slow), var(--transition-duration)',
				},
			},
			a: {
				display: 'block',
				transition: 'all var(--transition-duration) var(--transition-timing)',
				'&:hover': {
					color: getColor('ivory', 700),
					'.mainNav__dropdown__sectionHeader__text__longArrow': {
						opacity: 1,
						clipPath: 'inset(0 0 0 0)',
					},
				},
			},
		},
		ul: {
			listStyle: 'none',
			padding: 0,
			margin: remCalc(30),
			a: {
				display: 'block',
				transition: 'all var(--transition-duration-fast) var(--transition-timing)',
				[theme.breakpoints.down('sm')]: {
					...typography.h4,
				},
			},
			'a:link, a:visited': {
				textDecoration: 'none',
				fontFamily: 'var(--font-brandon-grotesque)',
				fontWeight: 400,
				lineHeight: '1.75',
				color: getColor('ivory', 700),
				backgroundPosition: '-6px center',
			},
			'a:hover, a:active': {
				filter: `brightness(0) saturate(100%) invert(18%) sepia(8%) saturate(547%) hue-rotate(292deg) brightness(98%) contrast(83%)`,
				color: getColor('white'),
				paddingLeft: remCalc(14),
				backgroundImage: `url('${chevron}')`,
				backgroundRepeat: 'no-repeat',
				backgroundPosition: 'left center',
			},
			[theme.breakpoints.up('sm')]: {
				columnCount: '2',
				margin: 'clamp(20px, calc(100vh * 0.03), 40px) 0 0 0',
			},
		},
		'.mainNav__dropdown__conditions__line': {
			display: 'flex',
			alignItems: 'center',
			'.mainNav__dropdown__conditions__line__beach': {
				gap: remCalc(20),
			},
			'.mainNav__dropdown__conditions__line__beach__conditions': {
				gap: remCalc(10),
				flexDirection: 'column',
				alignItems: 'flex-start',
				'.mainNav__dropdown__conditions__line__beach__conditions__primary': {
					fontWeight: 400,
					color: getColor('ivory', 700),
					lineHeight: remCalc(22),
					letterSpacing: '-0.32px',
				},
				'.mainNav__dropdown__conditions__line__beach__conditions__secondary': {
					fontWeight: 400,
					color: getColor('ivory', 600),
					lineHeight: remCalc(14),
				},
			},
			'.mainNav__dropdown__conditions__line__beach__flags': {
				gap: remCalc(10),
			},
			div: {
				display: 'flex',
				alignItems: 'center',
			},
			'.mainNav__dropdown__conditions__line__weather': {
				svg: {
					margin: remCalc('0 30 0 12'),
					width: remCalc(36),
				}
			},
		},
		'.mainNav__dropdown__sectionHeader__button': {
			display: 'none',
		},
		'.mainNav__dropdown__sectionHeader, .mainNav__dropdown__beachBayAccess': {
			display: 'flex',
			gap: remCalc(80),
			a: {
				color: getColor('ivory', 600),
				textDecoration: 'none',
				fontWeight: 400,
			},
		},
		[theme.breakpoints.down('sm')]: {
			'.mainNav__dropdown__sectionContainer': {
				'.mainNav__dropdown__sectionHeader': {
					padding: remCalc(30),
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: `1px solid ${getColor('ivory', 800)}`,
					color: getColor('ivory', 700),
					a: {
						color: getColor('ivory', 700),
					},
					'.mainNav__dropdown__sectionHeader__button': {
						flexGrow: 0,
						flexBasis: remCalc(45),
						display: 'block',
						padding: '0',
						margin: '0',
						fontSize: 0,
						textAlign: 'right',
						svg: {
							width: remCalc(45),
							color: getColor('ivory', 500),
						}
					},
				},
				'.mainNav__dropdown__sectionContent': {
					display: 'none',
					height: 'calc(100vh - 94px - 154px)',
					overflowY: 'auto',
				},

				'&.mainNav__dropdown__sectionContainer-notOpen': {
					display: 'none',
				},
				
				'&.mainNav__dropdown__sectionContainer-open': {
					'.mainNav__dropdown__sectionHeader': {
						flexDirection: 'column-reverse',
						alignItems: 'flex-start',
						'.mainNav__dropdown__sectionHeader__text': {
							...typography.h2,
						},
						'.mainNav__dropdown__sectionHeader__button': {
							transform: 'scale(-1)',
							marginBottom: remCalc(16),
						},
					},
					'.mainNav__dropdown__sectionContent': {
						display: 'block',
					},
				},
			},
			'.mainNav__dropdown__beachBayAccess': {
				padding: remCalc(30),
				borderBottom: `1px solid ${getColor('ivory', 800)}`,
				color: getColor('ivory', 700),
				a: {
					color: getColor('ivory', 700),
				},
			},
			'.mainNav__dropdown__conditions__line': {
				justifyContent: 'space-between',
				padding: remCalc('48 30'),
			},
		},
	},
}

const styles = {};

export default styles;